import { useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import VehicleImageLightbox, {
    normalizeVehicleImageUrls,
} from "../../Components/VehicleImageLightbox";
import { useMutation, useQuery } from "react-query";
import { getVehicleRequestDetails } from "../../Services/VehicleRequest";
import { requestDriverResubmit } from "../../Services/SignupRequest";
import { RejectDriverSignup, getExpiryFieldsToFix } from "../../Components/Modal/RejectDriverSignup";
import { Error, Success } from "../../Components/Modal";
import { format_date } from "../../Utils/helpers";
import BackButton from "../../Components/BackButton";
import Button from "../../Components/Button";
import DriverSummaryCard from "../../Components/DriverSummaryCard";
import "../Dashboard/Dashboard.css";

const formatExpiry = (value) => {
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return "Not provided";
    const daysLeft = Math.ceil((date.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
    const text = format_date(date, "DD/MM/YYYY");
    if (daysLeft <= 0) return `${text} (expired)`;
    if (daysLeft <= 30) return `${text} (${daysLeft} days left)`;
    return text;
};

export default function VehicleDetails() {
    const { id } = useParams();
    const [requestDetail, setRequestDetail] = useState({});
    const [viewerIndex, setViewerIndex] = useState(null);

    const imageUrls = useMemo(
        () => normalizeVehicleImageUrls(requestDetail?.vehicleImage),
        [requestDetail?.vehicleImage]
    );

    const { data, refetch } = useQuery(
        ["vehicleRequest_details", id],
        () => getVehicleRequestDetails(id)
    );

    useEffect(() => {
        const payload = data?.data?.data;
        if (payload == null) return;
        const detail = Array.isArray(payload) ? payload[0] : payload;
        if (detail) setRequestDetail(detail);
    }, [data?.data?.data]);

    const driver = requestDetail?.user;
    const driverId = driver?._id;
    const canRequestResubmit = Boolean(driverId && driver?.adminApproved);

    const { mutate: requestResubmit, isLoading: resubmitLoading } = useMutation(
        (payload) => requestDriverResubmit(driverId, payload),
        {
            onSuccess: (res) => {
                refetch();
                Success("Request Re-submit", res.data?.message || "Driver must update flagged documents.");
            },
            onError: (err) => {
                Error(err?.response?.data?.message || "Failed to request re-submit");
            },
        }
    );

    const openResubmitModal = () => {
        RejectDriverSignup(
            ({ reason, rejectedFields }) => {
                requestResubmit({ reason, rejectedFields });
            },
            {
                mode: "resubmit",
                title: "Ask for vehicle updates",
                confirmButtonText: "Send request",
                groups: ["Vehicle"],
                preselectedFields: getExpiryFieldsToFix({
                    insuranceExpiryDate: requestDetail?.insuranceExpiryDate,
                    registrationExpiryDate: requestDetail?.registrationExpiryDate,
                }),
            }
        );
    };

    const vehicleType = requestDetail?.type;
    const helperCount = Number(vehicleType?.helperCount || 0);

    const vehicleStats = [
        { label: "Vehicle Name", value: requestDetail?.name || "N/A" },
        { label: "Vehicle Type", value: vehicleType?.name || "N/A" },
        {
            label: "Max Capacity",
            value: vehicleType?.weight || vehicleType?.weightLimit
                ? `Up to ${vehicleType?.weight || vehicleType?.weightLimit} lbs`
                : "N/A",
        },
        {
            label: "Helpers Allowed",
            value: helperCount > 0 ? `${helperCount} Helper${helperCount > 1 ? "s" : ""}` : "None",
        },
        { label: "Brand Name", value: requestDetail?.brandName || "N/A" },
        { label: "Model Year", value: requestDetail?.year || "N/A" },
        { label: "Color", value: requestDetail?.color || "N/A" },
        { label: "Plate Number", value: requestDetail?.numberPlate || "N/A" },
        { label: "Insurance Expiry", value: formatExpiry(requestDetail?.insuranceExpiryDate) },
        { label: "Registration Expiry", value: formatExpiry(requestDetail?.registrationExpiryDate) },
        {
            label: "Request Date",
            value: requestDetail?.createdAt
                ? format_date(requestDetail.createdAt, "DD/MM/YYYY")
                : "N/A",
        },
    ];

    const statusClass =
        requestDetail?.status === "pending" ? "status-pending" : "status-active";

    return (
        <div className="app-content content dashboard">
            <div className="content-wrapper">
                <div className="content-body">
                    <section id="configuration">
                        <div className="user-details-wrapper">
                            <div className="row mb-4">
                                <div className="col-12">
                                    <div className="back-title d-flex flex-wrap align-items-center justify-content-between gap-3">
                                        <div className="d-flex align-items-center">
                                            <BackButton />
                                            <h1 className="pageTitle m-0 ms-3 fw-800 fs-2">Vehicle Details</h1>
                                        </div>
                                        <span className={`status-pill ${statusClass} text-uppercase fw-bold shadow-sm`}>
                                            {requestDetail?.status === "pending" ? "Pending" : "Active"}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="row mb-4 mb-lg-5">
                                <div className="col-12">
                                    <DriverSummaryCard driver={requestDetail?.user} />
                                </div>
                            </div>

                            <div className="row mb-4 mb-lg-5">
                                <div className="col-12">
                                    <div className="details-card-premium p-0 overflow-hidden">
                                        <div className="driver-section-header d-flex flex-wrap align-items-center justify-content-between gap-2">
                                            <h3 className="driver-docs-title mb-0">Vehicle Information</h3>
                                            {canRequestResubmit && (
                                                <Button
                                                    type="button"
                                                    className="request-resubmit-btn"
                                                    loading={resubmitLoading}
                                                    onClick={openResubmitModal}
                                                >
                                                    Request Re-submit
                                                </Button>
                                            )}
                                        </div>
                                        <div className="user-profile-body pt-0">
                                            <div className="user-profile-stats mb-0 border-0 pt-0">
                                                {vehicleStats.map((item) => (
                                                    <div key={item.label} className="user-profile-stat">
                                                        <span className="user-profile-stat-label">{item.label}</span>
                                                        <span className="user-profile-stat-value">{item.value}</span>
                                                    </div>
                                                ))}
                                            </div>

                                            <div className="driver-docs-section mb-0 pb-0">
                                                <h4 className="driver-docs-subtitle">Vehicle Images</h4>
                                                {imageUrls.length > 0 ? (
                                                    <div className="driver-vehicle-images">
                                                        {imageUrls.map((src, idx) => (
                                                            <button
                                                                key={src + idx}
                                                                type="button"
                                                                className="driver-doc-card driver-doc-card--compact"
                                                                onClick={() => setViewerIndex(idx)}
                                                            >
                                                                <img src={src} alt={`Vehicle ${idx + 1}`} />
                                                            </button>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="driver-doc-empty driver-doc-empty--inline">
                                                        No vehicle images uploaded
                                                    </div>
                                                )}
                                                <VehicleImageLightbox
                                                    show={viewerIndex !== null}
                                                    urls={imageUrls}
                                                    startIndex={viewerIndex ?? 0}
                                                    onHide={() => setViewerIndex(null)}
                                                />
                                            </div>

                                            {(requestDetail?.status === "rejected" || requestDetail?.status === "cancelled") && requestDetail?.rejectedReason && (
                                                <div className="vehicle-rejection-note">
                                                    <span className="vehicle-rejection-note__label">Rejection Reason</span>
                                                    <p className="vehicle-rejection-note__text">{requestDetail.rejectedReason}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}
